import React from 'react';
import { Button, Box, ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BreakfastColors, BreakfastConstants } from '../../theme/BreakfastTheme';

type StyledButtonProps = Omit<ButtonProps, 'style' | 'size'> & {
  buttonStyle: BreakfastButtonStyle;
  buttonSize: BreakfastButtonSize;
  destructive: boolean;
  shrinkWrap: boolean;
};

export enum BreakfastButtonStyle {
  Primary = 'primary',
  Secondary = 'secondary'
}

export enum BreakfastButtonSize {
  Small = 'small',
  Large = 'large'
}

interface BreakfastButtonProps {
  title: string;
  style: BreakfastButtonStyle;
  size: BreakfastButtonSize;
  onPressed?: () => void;
  leftIcon?: React.ReactNode;
  destructive?: boolean;
  shrinkWrap?: boolean;
  disabled?: boolean;
  component?: React.ElementType;
}

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'buttonStyle' &&
    prop !== 'buttonSize' &&
    prop !== 'destructive' &&
    prop !== 'shrinkWrap',
})<StyledButtonProps>(({ buttonStyle, buttonSize, destructive, shrinkWrap }) => {
  const isSmall = buttonSize === BreakfastButtonSize.Small;
  const isPrimary = buttonStyle === BreakfastButtonStyle.Primary;
  
  let backgroundColor: string;
  let color: string;
  let borderColor: string | undefined;
  
  if (destructive) {
    backgroundColor = isPrimary ? BreakfastColors.error : BreakfastColors.errorContainer;
    color = isPrimary ? '#FFFFFF' : BreakfastColors.error;
    borderColor = isPrimary ? undefined : BreakfastColors.error + '4D'; // 30% opacity
  } else {
    backgroundColor = isPrimary ? BreakfastColors.primary : BreakfastColors.defaultFill;
    color = isPrimary ? '#FFFFFF' : BreakfastColors.primary;
    borderColor = isPrimary ? undefined : BreakfastColors.primary + '33'; // 20% opacity
  }
  
  return {
    backgroundColor,
    color,
    border: borderColor ? `1px solid ${borderColor}` : 'none',
    borderRadius: isSmall ? BreakfastConstants.cornerRadius : 104,
    padding: isSmall ? '8px 16px' : '8px 24px', // Match Flutter: vertical padding contributes to total height
    minWidth: shrinkWrap ? 'auto' : (isSmall ? 80 : 120),
    height: isSmall ? 36 : 56,
    fontSize: isSmall ? '14px' : '16px',
    fontWeight: 700,
    fontFamily: '"Circular LL VIP", "CircularXX", sans-serif',
    letterSpacing: isSmall ? '-0.1px' : '-0.2px',
    textTransform: 'none',
    boxShadow: 'none',
    width: shrinkWrap ? 'auto' : '100%',
    '&:hover': {
      backgroundColor: destructive 
        ? (isPrimary ? BreakfastColors.error + 'E6' : BreakfastColors.errorContainer + 'B3')
        : (isPrimary ? BreakfastColors.primary + 'E6' : BreakfastColors.defaultFill),
      boxShadow: 'none',
    },
    '&:disabled': {
      backgroundColor: isPrimary ? BreakfastColors.subtitle + '33' : BreakfastColors.defaultFill,
      color: BreakfastColors.subtitle,
      border: borderColor ? `1px solid ${BreakfastColors.subtitle}33` : 'none',
    },
  };
});

export const BreakfastButton: React.FC<BreakfastButtonProps> = ({
  title,
  style,
  size,
  onPressed,
  leftIcon,
  destructive = false,
  shrinkWrap = false,
  disabled = false,
  component = 'button',
}) => {
  const isLabelTarget = component !== 'button';

  return (
    <StyledButton
      component={component}
      buttonStyle={style}
      buttonSize={size}
      destructive={destructive}
      shrinkWrap={shrinkWrap}
      onClick={isLabelTarget ? undefined : onPressed}
      disabled={!isLabelTarget && (disabled || !onPressed)}
      aria-disabled={isLabelTarget && disabled ? true : undefined}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={shrinkWrap ? 'auto' : '100%'}
        gap={leftIcon ? 1 : 0}
      >
        {leftIcon && (
          <Box display="flex" alignItems="center">
            {leftIcon}
          </Box>
        )}
        {title}
      </Box>
    </StyledButton>
  );
};
